// services/reportsApi.ts
import apiClient from "./apiClient";

export interface SalesReportInvoice {
  id: number;
  invoiceNo: string;
  customerId: number;
  organizationId: number;
  invoiceDate: string;
  totalAmount: string;
  discount: string;
  taxAmount: string;
  grandTotal: string;
  status: string;
  printed: boolean;
  Customer: {
    id: number;
    fullName: string;
    phone: string;
  };
}

export interface SalesReportResponse {
  success: boolean;
  summary: {
    totalInvoiceCount: number;
    totalInvoiceAmount: string;
    totalTaxAmount: string;
    totalGrandAmount: string;
  };
  invoices: SalesReportInvoice[];
}

// services/reportsApi.ts – add these methods
export interface ProductRequirementItem {
  productId: number;
  productName: string;
  unitSymbol: string;
  totalQuantity: number;
  totalWeight: string;
}

export interface ProductRequirementResponse {
  success: boolean;
  startDate: string;
  endDate: string;
  employeeName?: string | null; // new
  customerName?: string | null;
  products: ProductRequirementItem[];
}

export interface PaymentReportItem {
  slNo: number;
  customerName: string;
  amount: string;
  paymentType: string;
  paymentDate: string;
  referenceNo: string | null;
  createdBy: string;
}

export interface PaymentReportSummary {
  totalPayments: number;
  totalAmount: string;
  totalCash: string;
  totalBank: string;
  totalDamage: string;
}

export interface ReturnReportItem {
  slNo: number;
  invoiceNo: string;
  customerName: string;
  productName: string;
  quantity: number;
  amount: string;
  status: "pending" | "approved" | "rejected";
  returnDate: string;
  reason: string;
}

export interface ReturnReportSummary {
  totalReturns: number;
  totalQuantity: number;
  totalAmount: string;
}

export const reportsApi = {
  getSalesReport: async (params: {
    startDate?: string;
    endDate?: string;
    invoiceNo?: string;
    customerId?: number;
    customerName?: string;
    areaId?: number;
    createdBy?: number;
  }): Promise<{ summary: any; invoices: SalesReportInvoice[] }> => {
    const response = await apiClient.get("/reports/sales", { params });
    return response.data;
  },

  downloadSalesReportPdf: async (params: {
    startDate?: string;
    endDate?: string;
    invoiceNo?: string;
    customerId?: number;
    customerName?: string;
    areaId?: number;
    createdBy?: number;
  }): Promise<Blob> => {
    const response = await apiClient.get("/reports/sales/download", {
      params,
      responseType: "blob",
    });
    return response.data;
  },
  getProductRequirementReport: async (params: {
    today?: boolean;
    startDate?: string;
    endDate?: string;
    createdBy?: string;
    customerId?: string;
    areaId?: string; // new
  }): Promise<ProductRequirementResponse> => {
    const response = await apiClient.get("/reports/product-requirement", {
      params,
    });
    return response.data;
  },
  downloadProductRequirementPdf: async (params: {
    today?: boolean;
    startDate?: string;
    endDate?: string;
    createdBy?: string;
    customerId?: string;
    areaId?: string; // new
  }): Promise<Blob> => {
    const response = await apiClient.get(
      "/reports/product-requirement/download",
      {
        params,
        responseType: "blob",
      },
    );
    return response.data;
  },
  getStockValueReport: async (params: {
    page?: number;
    limit?: number;
    productId?: number;
    search?: string;
  }) => {
    const response = await apiClient.get("/reports/stock-value", { params });
    return response.data; // { products, pagination, summary }
  },

  downloadStockValueReportPdf: async (params?: {
    productId?: number;
    search?: string;
  }) => {
    const response = await apiClient.get("/reports/stock-value/download", {
      params,
      responseType: "blob",
    });
    return response.data;
  },
  getPaymentReport: async (params: {
    startDate?: string;
    endDate?: string;
    customerId?: number;
    paymentType?: string;
    createdBy?: number;
  }): Promise<{ summary: PaymentReportSummary; data: PaymentReportItem[] }> => {
    const response = await apiClient.get("/reports/payments", { params });
    return response.data;
  },

  downloadPaymentReportPdf: async (params: {
    startDate?: string;
    endDate?: string;
    customerId?: number;
    paymentType?: string;
    createdBy?: number;
  }): Promise<Blob> => {
    const response = await apiClient.get("/reports/payments/download", {
      params,
      responseType: "blob",
    });
    return response.data;
  },

  getReturnReport: async (params: {
    startDate?: string;
    endDate?: string;
    customerId?: number;
    customerName?: string;
    status?: string;
  }): Promise<{ summary: ReturnReportSummary; data: ReturnReportItem[] }> => {
    const response = await apiClient.get("/reports/returns", { params });
    return response.data;
  },

  downloadReturnReportPdf: async (params: {
    startDate?: string;
    endDate?: string;
    customerId?: number;
    customerName?: string;
    status?: string;
  }): Promise<Blob> => {
    const response = await apiClient.get("/reports/returns/download", {
      params,
      responseType: "blob",
    });
    return response.data;
  },

  downloadStockAdditionReportPdf: async (params: {
    startDate?: string;
    endDate?: string;
    productId?: number;
  }): Promise<Blob> => {
    const response = await apiClient.get("/reports/stock-addition/download", {
      params,
      responseType: "blob",
    });
    return response.data;
  },
};
